import React, { useCallback, useEffect, useRef, useState } from 'react';
import quickBooksService, { validateQuickBooksLink } from '../services/quickBooksService';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../utils/authSession';
import { useT } from '../i18n/LanguageContext';

const STATUS_ERROR = 'Could not read QuickBooks setup. Refresh and try again.';
const CONNECT_ERROR = 'Could not create a QuickBooks authorization link. Please try again or contact your administrator.';
const EXPIRED = 'This QuickBooks link has expired. Create a new link.';
const SESSION_ENDED = 'Your session changed. Sign in again to reconnect QuickBooks.';

/** Mounted only by the existing admin-only Integrations route. Backend endpoints also enforce admin. */
export default function QuickBooksConnectionCard() {
  const t = useT();
  const runRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState(null);
  const [link, setLink] = useState(null);
  const current = useCallback((run) => Boolean(run?.alive && runRef.current === run && isSessionCurrent(run.binding)), []);

  const refresh = useCallback(async (run) => {
    if (!current(run) || run.busy) return;
    run.busy = true;
    setLoading(true);
    setLink(null);
    setError(null);
    try {
      const next = await quickBooksService.getStatus(run.binding);
      if (current(run)) setStatus(next);
    } catch {
      if (current(run)) { setStatus(null); setError(STATUS_ERROR); }
    } finally {
      run.busy = false;
      if (current(run)) setLoading(false);
    }
  }, [current]);

  useEffect(() => {
    const run = { binding: captureSession(), alive: true, busy: false };
    runRef.current = run;
    refresh(run);
    const sessionChanged = () => {
      if (isSessionCurrent(run.binding)) return;
      run.alive = false;
      setStatus(null);
      setLink(null);
      setLoading(false);
      setConnecting(false);
      setEnded(true);
      setError(SESSION_ENDED);
    };
    window.addEventListener(SESSION_EVENT, sessionChanged);
    window.addEventListener('storage', sessionChanged);
    return () => {
      run.alive = false;
      window.removeEventListener(SESSION_EVENT, sessionChanged);
      window.removeEventListener('storage', sessionChanged);
    };
  }, [refresh]);

  useEffect(() => {
    if (!link) return undefined;
    const timer = setTimeout(() => { setLink(null); setError(EXPIRED); }, Math.max(0, Date.parse(link.expiresAt) - Date.now()));
    return () => clearTimeout(timer);
  }, [link]);

  const connect = async () => {
    const run = runRef.current;
    if (!current(run) || run.busy || status?.connectConfigured !== true) return;
    run.busy = true;
    setConnecting(true);
    setLink(null);
    setError(null);
    try {
      const next = await quickBooksService.beginConnect(run.binding);
      if (current(run)) setLink(validateQuickBooksLink(next));
    } catch {
      if (current(run)) setError(CONNECT_ERROR);
    } finally {
      run.busy = false;
      if (current(run)) setConnecting(false);
    }
  };

  const openConsent = (event) => {
    if (!current(runRef.current)) {
      event.preventDefault();
      setLink(null);
      setEnded(true);
      setError(SESSION_ENDED);
      return;
    }
    try { validateQuickBooksLink(link); } catch {
      event.preventDefault();
      setLink(null);
      setError(EXPIRED);
    }
  };

  const buttonStyle = 'rounded-lg border border-[#cbd5e1] px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed';
  return (
    <section aria-labelledby="quickbooks-connection-heading" className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-6 mb-6">
      <h2 id="quickbooks-connection-heading" className="text-[16px] font-semibold text-[#0f172a]">QuickBooks</h2>
      <p className="mt-1 text-[13px] text-[#64748b]">{t('Reconnect the existing MedEffects company through Intuit.')}</p>
      <p className="mt-3 text-[13px] text-[#64748b]">{t('This page shows saved setup information. It does not test QuickBooks access.')}</p>
      <div className="mt-3 text-sm" aria-live="polite">
        {loading ? <p>{t('Reading QuickBooks setup...')}</p> : status && (
          <>
            <p>{t(status.configured ? 'QuickBooks credentials are configured.' : 'QuickBooks setup is incomplete. Reconnect if available below.')}</p>
            <p className="mt-1">{t(status.environment === 'sandbox' ? 'QuickBooks environment: sandbox.' : 'QuickBooks environment: production.')}</p>
            {!status.connectConfigured && <p className="mt-1">{t('QuickBooks reconnect is unavailable. Contact your administrator.')}</p>}
          </>
        )}
      </div>
      <p className="mt-4 text-sm font-semibold text-[#0f172a]">{t('In Intuit, select the existing MedEffects company. Do not create a new company.')}</p>
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{t(error)}</p>}
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className={`${buttonStyle} bg-[#0f172a] text-white`} onClick={connect}
          disabled={ended || loading || connecting || status?.connectConfigured !== true}>
          {t(connecting ? 'Creating QuickBooks link...' : 'Create QuickBooks reconnect link')}
        </button>
        <button type="button" className={`${buttonStyle} text-[#334155]`} onClick={() => refresh(runRef.current)} disabled={ended || loading || connecting}>
          {t('Refresh QuickBooks setup')}
        </button>
      </div>
      {link && (
        <div className="mt-4 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4">
          <a href={link.authorizeUrl} onClick={openConsent} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
            className="inline-flex rounded-lg bg-[#0f172a] px-4 py-2 text-sm font-semibold text-white">
            {t('Open Intuit consent')}
          </a>
          <p className="mt-2 text-[13px] text-[#64748b]">{t('The single-use link expires in 10 minutes. After consent, return here and refresh QuickBooks setup.')}</p>
        </div>
      )}
    </section>
  );
}
